/**
 * English. The fallback locale.
 *
 * Kept in step with `nl.ts` key for key. A key present here and missing there shows an
 * operator English rather than a raw key, which is the point of the fallback: seeing
 * `schedule.readiness.blocked` during an incident is worse than seeing it in the wrong
 * language.
 */
export default {
  common: {
    yes: 'Yes',
    no: 'No',
    all: 'All',
    cancel: 'Cancel',
    save: 'Save',
    loading: 'Loading',
    unknown: 'Unknown',
    today: 'Today',
    previous: 'Previous',
    next: 'Next',
    day: 'Day',
    week: 'Week',
    active: 'Active',
    archived: 'Archived',
    minutes: '{count} min',
    signOut: 'Sign out',
    language: 'Language',
    days: '{count} days',
    showing: 'Showing {count} of {total}',
    showingLatest: 'Showing the latest {count}',
    showingRange: '{from}–{to} of {total}',
    loadMore: 'Load more',
    wait: 'Wait…',
    now: 'Now',
    to: 'to',
    readOnly: 'Read-only',
  },

  units: {
    minutes: '{count} min',
    seconds: '{count} s',
    hoursMinutes: '{hours} h {minutes} min',
    daysHours: '{days} d {hours} h',
    minuteShort: 'm',
  },

  contentTypes: {
    unknown: '—',
    show: 'Show',
    music: 'Music',
    jingle: 'Jingle',
    sweeper: 'Sweeper',
    commercial: 'Commercial',
    promo: 'Promo',
    news: 'News',
    podcast: 'Podcast',
    live: 'Live',
    emergency: 'Emergency',
  },

  nav: {
    onAir: 'On air',
    programming: 'Programming',
    media: 'Media',
    podcasts: 'Podcasts',
    audience: 'Audience',
    operations: 'Operations',
    station: 'Station',

    dashboard: 'Dashboard',
    timeline: 'Playout timeline',
    requests: 'Requests',
    liveStudio: 'Live studio',
    schedule: 'Schedule',
    clocks: 'Radio clocks',
    shows: 'Shows',
    episodes: 'Episodes',
    playlists: 'Playlists',
    playlistStudio: 'Playlist Studio',
    library: 'Library',
    uploads: 'Uploads',
    recordings: 'Recordings',
    archive: 'Archive',
    distribution: 'Distribution',
    analytics: 'Analytics',
    liveListeners: 'Live listeners',
    streamHealth: 'Stream health',
    systemHealth: 'System health',
    alerts: 'Alerts',
    jobs: 'Jobs',
    auditLog: 'Audit log',
    domains: 'Own domain',
    streams: 'Streams & mounts',
    streamers: 'DJs / streamers',
    fallback: 'Fallback & emergency',
    users: 'Users & permissions',
    settings: 'Settings',
    notifications: 'Alerting',
    apiKeys: 'API keys',

    collapse: 'Collapse',
    expand: 'Expand',
    tagline: 'Podcast & Radio Platform',
  },

  shell: {
    skipToControls: 'Skip to on-air controls',
    apiUnreachable: 'The panel cannot reach the API',
    apiUnreachableDetail:
      'On-air state below may be out of date. The stream itself is unaffected by this.',
    componentsNeedAttention: '1 component needs attention | {count} components need attention',
    restoring: 'Restoring your session…',
    offlineDetail: 'No source is carrying the stream. Check the playout runtime.',
    stationTime: 'Station time',
    yourAccount: 'Your account and security',
  },

  player: {
    monitor: 'Monitor the stream',
    stopMonitoring: 'Stop monitoring',
    mute: 'Mute monitoring',
    unmute: 'Unmute monitoring',
    nothingOnAir: 'Nothing on air',
    listeners: 'listeners',
    progress: 'Progress through the current item',
    live: 'Live',
    fallback: 'Fallback',
    offline: 'Offline',
    auto: '24/7',
  },

  onAir: {
    runtimeQueues: 'Runtime queues',
    runtimeQueuesIntro:
      'What the playout runtime is holding to push. These are not the rotation above: clearing one empties what is pending and leaves the item on air playing.',
    queueLength: '{count} waiting',
    clearQueue: 'Clear',
    clearQueueConfirm: 'Empty this queue',
    queueName: {
      scheduled: 'Scheduled',
      manual: 'Manual',
      traffic: 'Traffic',
    },
    queue: 'Queue (rotation)',
    scheduled: 'Scheduled',
    title: 'On air',
    skip: 'Skip current item',
    emergency: 'Activate emergency source',
    emergencyConfirm: 'Go to emergency',
    clearEmergency: 'Clear the emergency source',
    reasonRequired: 'A reason is required',
    nextUp: 'Next up',
    stream: 'Stream',
    runtimeHealth: 'Runtime health',
    mount: 'Mount',
    format: 'Format',
    elapsed: 'Elapsed',
    remaining: 'Remaining',
    skipConfirm: 'Skip now',
    nothingScheduled: 'Nothing scheduled',
    nothingScheduledDetail:
      'The station will continue on general rotation until something is scheduled.',
    noRuntimeReports: 'No runtime reports yet',
    noRuntimeReportsDetail: 'The playout runtime has not reported its state to the panel.',
    listeners: 'Listeners',
    unnamedItem: 'Unnamed item',
    noMedia: 'No playable media is linked to this slot yet',
    stereo: 'stereo',
    mono: 'mono',
    rerun: 'Rerun',
  },

  schedule: {
    title: 'Schedule',
    intro:
      'Colour shows whether a slot can actually air. A block with an exact time is never slid.',
    range: 'Range',
    slotsNeedAttention: '1 slot needs attention | {count} slots need attention',
    overlaps: 'Overlaps another slot',
    airs: 'Airs',
    kind: 'Kind',
    status: 'Status',
    readiness: 'Readiness',
    exactTimeNote:
      'This is an exact-time item. The scheduler may never slide it to absorb an overrun.',
    couldNotLoad: 'The schedule could not be loaded',
    couldNotMove: 'That slot could not be moved',
    freezeBlocked:
      'That slot is inside the {minutes} minute freeze window before air, and you do not have override authority.',
    overrideTitle: 'This slot is close to air',
    overrideBody:
      '{show} airs within {minutes} minutes. Moving it now is recorded against your account with the reason you give.',
    overrideLabel: 'Why are you moving it?',
    overridePlaceholder: 'Episode arrived late from the studio',
    overrideConfirm: 'Move and record the reason',
    unnamedSlot: 'Unnamed slot',
    markers: {
      exactTime: 'Exact time',
      rerun: 'Rerun',
      locked: 'Inside the freeze window',
    },
    kinds: {
      PLANNED: 'Planned',
      RESERVED: 'Reserved',
      LIVE: 'Live',
      SPECIAL: 'Special',
      RERUN: 'Rerun',
      AD_BREAK: 'Ad break',
    },
    statuses: {
      SCHEDULED: 'Scheduled',
      CONFIRMED: 'Confirmed',
      BROADCASTING: 'On air',
      BROADCAST: 'Broadcast',
      CANCELLED: 'Cancelled',
    },
    readinessState: {
      READY: 'Ready',
      PENDING: 'Pending',
      BLOCKED: 'Blocked',
      AIRED: 'Aired',
      NOT_APPLICABLE: 'Not applicable',
    },
  },

  timeline: {
    title: 'Playout timeline',
    intro:
      'What was planned and what actually aired, on the same axis. An item that drifted or never ran is marked on both tracks.',
    window: 'Window',
    hours: '±{count} hours',
    planned: 'Planned',
    asRun: 'As run',
    discrepancies: '1 item did not air as planned | {count} items did not air as planned',
    late: '{seconds}s late',
    early: '{seconds}s early',
    exactTimeItem: 'Exact-time item',
    warning: 'Warning',
    states: {
      aired: 'Aired',
      on_air: 'On air',
      queued: 'Queued',
      locked: 'Locked',
      blocked: 'Blocked',
    },
    couldNotLoad: 'The timeline could not be loaded',
  },

  shows: {
    create: 'New show',
    createTitle: 'New show',
    createHint:
      'A name and an address are enough. The weekday and length decide how the show appears in the schedule once scheduling is on.',
    name: 'Name',
    slug: 'Address (in URLs, lowercase words and hyphens)',
    description: 'Description',
    weekday: 'Broadcast day',
    noWeekday: 'No fixed day (one-off)',
    duration: 'Length (minutes)',
    createConfirm: 'Create show',
    manageTitle: 'Manage',
    manageIntro:
      'Scheduling decides whether this show puts itself in the schedule. Archiving takes it out of the list; it can always be undone.',
    turnSchedulingOn: 'Turn scheduling on',
    turnSchedulingOff: 'Turn scheduling off',
    schedulingOnWarning:
      'With scheduling on, {name} reaches the schedule and the transmitter without anybody touching it.',
    schedulingOffWarning: 'With scheduling off, the schedule makes no new slots for {name}.',
    schedulingReason: 'Reason (goes in the audit log)',
    schedulingConfirm: 'Confirm',
    schedulingNowOn: 'Scheduling is on for {name}.',
    schedulingNowOff: 'Scheduling is off for {name}.',
    archive: 'Archive',
    archiveWarning:
      '{name} leaves the active list and the schedule. It can always be brought back.',
    archiveConfirm: 'Yes, archive {name}',
    archiveDone: '{name} has been archived.',
    cancel: 'Cancel',
    title: 'Shows',
    intro:
      'A show generates slots only when scheduling is switched on. That is deliberate: a show that starts scheduling itself by accident puts unintended audio on air.',
    which: 'Which shows',
    schedulingOn: 'Scheduling on',
    schedulingOff: 'Scheduling off',
    empty: 'No shows yet.',
    emptyArchived: 'No archived shows.',
    deleteTitle: 'Ending a show for good',
    delete: 'Delete',
    deleteIntro:
      'Archiving keeps a show and its history and takes it off the schedule. This removes it, along with its episodes, its planned broadcasts and its podcast feed. A feed that is still published has to be unpublished first.',
    deleteWarning:
      'Deleting {name} removes its episodes, its planned broadcasts and its podcast feed. There is no undo.',
    deleteReason: 'Why',
    deletePassword: 'Your password',
    deleteConfirm: 'Delete {name} permanently',
    deleteDone: 'Deleted {name}, with {episodes} episodes and {slots} planned broadcasts.',
    open: 'Open {name}',
    oneOff: 'One-off',
    everyDay: 'Every {day}',
    everyOtherDay: 'Every other {day}',
    everyNthDay: 'Every {interval} weeks on {day}',
    daily: 'Daily',
    everyNDays: 'Every {interval} days',
    monthly: 'Monthly',
    everyNMonths: 'Every {interval} months',
  },

  episodes: {
    create: 'New episode',
    hideForm: 'Close the form',
    createHint:
      'The record first, then the audio: attach it to this episode under Uploads once the file exists.',
    show: 'Show',
    pickShow: 'Pick a show',
    number: 'Episode number',
    episodeTitle: 'Title',
    plannedDate: 'Planned broadcast date',
    specialOption: 'Special (outside the regular numbering, needs approval)',
    createConfirm: 'Create episode',
    created: 'Episode {number} {title} has been created.',
    title: 'Episodes',
    intro:
      'An episode is ready when its audio has passed analysis, not when somebody marked it ready. The audio column is the one that decides whether it can air.',
    columnEpisode: 'Episode',
    columnShow: 'Show',
    columnPlanned: 'Planned',
    columnAudio: 'Audio',
    columnStatus: 'Status',
    special: 'Special',
    needsApproval: 'Needs approval',
    missing: 'Missing',
    empty: 'No episodes',
    emptyDetail: 'Upload audio and create an episode to schedule it.',
    caption: 'Episodes',
    statuses: {
      DRAFT: 'Draft',
      COMPLETED: 'Aired',
      ARCHIVED: 'Archived',
    },
    packTitles: '{count} title changes',
    packMismatch: 'Episode pack: duration mismatch',
    tracklist: 'Tracklist (CSV)',
  },

  playlists: {
    title: 'Playlists',
    intro:
      'Higher priority wins when several playlists are eligible. The emergency playlist is never offered to rotation: it exists only for the fallback source.',
    columnPlaylist: 'Playlist',
    columnType: 'Type',
    columnPriority: 'Priority',
    columnOrder: 'Order',
    columnItems: 'Items',
    columnState: 'State',
    disabled: 'Disabled',
    empty: 'Empty',
    activeState: 'Active',
    interrupts: 'Interrupts',
    emptyTitle: 'No playlists',
    emptyDetail: 'Create a playlist to give rotation something to draw from.',
    caption: 'Playlists',
    types: {
      GENERAL_ROTATION: 'General rotation',
      SCHEDULED: 'Scheduled',
      ONCE_PER_X_SONGS: 'Once every X songs',
      ONCE_PER_X_MINUTES: 'Once every X minutes',
      ONCE_PER_HOUR: 'Once per hour',
      JINGLE: 'Jingle',
      COMMERCIAL: 'Commercial',
      EMERGENCY: 'Emergency',
    },
    orderModes: {
      SEQUENTIAL: 'Sequential',
      SHUFFLE: 'Shuffled',
      RANDOM: 'Random',
      WEIGHTED: 'Weighted',
    },
  },

  media: {
    columnTrack: 'Track',
    columnArtist: 'Artist',
    untitled: 'Untitled',
    editTags: 'Edit',
    title: 'Media library',
    intro:
      'Every file is analysed on upload. A file that does not decode is quarantined rather than scheduled, because finding out on air is not an option.',
    columnFile: 'File',
    columnLength: 'Length',
    columnFormat: 'Format',
    columnLoudness: 'Loudness',
    columnAnalysis: 'Analysis',
    columnHotCache: 'Hot cache',
    staged: 'Staged',
    notStaged: 'Not staged',
    emptyTitle: 'No media yet',
    emptyDetail: 'Upload audio to build the library.',
    caption: 'Media assets',
    status: {
      PENDING: 'Awaiting analysis',
      ANALYZING: 'Analysing',
      READY: 'Ready',
      QUARANTINED: 'Quarantined',
      MISSING: 'Missing',
    },
    staging: {
      NOT_STAGED: 'Not staged',
      STAGING: 'Staging',
      STAGED: 'Staged',
      FAILED: 'Staging failed',
      EVICTED: 'Evicted from cache',
    },
  },

  alerts: {
    title: 'Alerts',
    intro:
      'Acknowledging says somebody is looking. Resolving says the condition is gone. They are separate because conflating them is how an incident gets forgotten.',
    open: 'Open',
    everything: 'Everything',
    columnSeverity: 'Severity',
    columnAlert: 'Alert',
    columnComponent: 'Component',
    columnSeen: 'Seen',
    columnLastSeen: 'Last seen',
    columnState: 'State',
    acknowledge: 'Acknowledge',
    resolve: 'Resolve',
    acknowledged: 'Acknowledged',
    resolved: 'Resolved',
    openState: 'Open',
    emptyTitle: 'Nothing is wrong',
    emptyDetail: 'No alert is open. This is the state you want.',
    caption: 'Alerts',
    resolveConfirm: 'Resolve alert',
    severity: {
      CRITICAL: 'Critical',
      ERROR: 'Error',
      WARNING: 'Warning',
      INFO: 'Info',
    },
  },

  audit: {
    title: 'Audit log',
    intro:
      'Every privileged action, with who did it and why. Entries are written by the services that perform the action and cannot be edited here.',
    window: 'Window',
    hours24: '24 hours',
    days: '{count} days',
    columnWhen: 'When',
    columnWho: 'Who',
    columnAction: 'Action',
    columnOn: 'On',
    columnReason: 'Reason',
    emptyTitle: 'Nothing recorded',
    emptyDetail: 'No privileged action was taken in this window.',
    caption: 'Audit events',
  },

  health: {
    title: 'System health',
    intro: 'Per component, with how long ago it last reported itself.',
    components: 'Components',
    noReports: 'No health reports',
    noReportsDetail: 'Nothing has reported a component state yet.',
    stale: 'Health could not be refreshed; this is the last known state.',
  },

  streams: {
    title: 'Streams & mounts',
    intro:
      'Clients query this list rather than hardcoding a mount, so changing a codec or a bitrate does not require an app release.',
    columnName: 'Name',
    columnCodec: 'Codec',
    columnBitrate: 'Bitrate',
    columnMount: 'Mount',
    columnAddress: 'Listen URL',
    columnDefault: 'Default',
    default: 'Default',
    emptyTitle: 'No streams configured',
    emptyDetail: 'Seed the station or add an output to publish a stream.',
    caption: 'Available stream profiles',
    columnState: 'State',
    disabled: 'Off',
    enable: 'Enable',
    enableConfirm: 'Enable stream',
    disable: 'Disable',
    disableConfirm: 'Disable stream',
    promote: 'Make default',
    promoteConfirm: 'Set as default',
  },

  login: {
    email: 'Email address',
    password: 'Password',
    totp: 'Verification code',
    signIn: 'Sign in',
    signingIn: 'Signing in',
    failed: 'Sign-in failed',
    trustDevice: 'Remember this device',
    trustDeviceDetail: 'Trust this device and skip the code for 30 days',
    totpPrompt: 'Enter the six-digit code from your authenticator app.',
    recoveryCode: 'Recovery code',
    recoveryPrompt: 'Enter one of your recovery codes. Each one works once, then expires.',
    useRecoveryCode: 'Lost your authenticator? Use a recovery code',
    useAuthenticator: 'Use the authenticator after all',
    failedRetry: 'Sign-in failed. Try again.',
    verify: 'Verify',
    verifying: 'Verifying…',
    startAgain: 'Start again',
    errors: {
      invalidCredentials: 'That email address and password do not match.',
      totpInvalid: 'That verification code is not correct.',
      totpReplayed: 'That code has already been used. Wait for the next one.',
      locked: 'Too many attempts. This account is locked for a few minutes.',
      disabled: 'This account is disabled. Ask an administrator.',
      expired: 'This sign-in attempt expired. Start again.',
      network: 'The panel cannot reach the API.',
      generic: 'Something went wrong. Try again.',
    },
  },

  stations: {
    switch: 'Switch station',
    none: 'No station',
    label: 'Station',
  },

  domains: {
    title: 'Your own domain',
    intro:
      'Let your listeners reach the stream at a name of your own, such as stream.mystation.com, instead of an address of ours.',
    hostname: 'Domain name',
    add: 'Add',
    addHint:
      'A subdomain works best. Once added you get two DNS records to publish at your registrar.',
    none: 'No domain of your own for this station yet.',
    live: 'Live',
    pending: 'Waiting for DNS',
    liveDetail: 'The certificate is handled automatically; there is nothing for you to install.',
    publish:
      'Publish these two records in your DNS. The CNAME sends listeners here; the TXT proves the name is yours.',
    recordType: 'Type',
    recordName: 'Name',
    recordValue: 'Value',
    copy: 'Copy',
    copied: 'Copied',
    check: 'Check DNS',
    recheck: 'Check again',
    notYet: 'Not ready yet',
    remove: 'Remove',
  },

  organisation: {
    title: 'Organisation',
    intro:
      'Your organisation, its plan, and who can reach it. The usage shown here is the same number every limit is measured against.',
    stations: 'Stations',
    seats: 'Seats',
    storage: 'Storage',
    listeners: 'Peak listeners per station',
    quality: 'Stream quality',
    unlimited: 'Unlimited',
    noneTitle: 'You do not belong to an organisation.',
    noneDetail: 'Ask the owner of your organisation to add your account.',
    createTitle: 'New organisation',
    create: 'Create an organisation',
    createHint:
      'An organisation is a customer: its own stations, its own people, its own plan. The first owner has to have an account already; leave it empty and it is you.',
    orgName: 'Name',
    orgSlug: 'Address (derived from the name when left empty)',
    ownerEmail: 'Email of the first owner',
    createConfirm: 'Create',
    allTitle: 'All organisations',
    stationsTitle: 'Stations',
    noStations: 'No station yet.',
    addStation: 'Add a station',
    addStationHint:
      'Creating a station is only a database row. The broadcast side picks it up within a minute.',
    stationName: 'Name',
    stationSlug: 'Address (in the stream URL)',
    addStationConfirm: 'Create station',
    membersTitle: 'Who has access',
    addMember: 'Add somebody',
    addMemberHint:
      'The account has to exist already: there is no mail server, so an invitation would be one nobody ever sees.',
    addMemberConfirm: 'Add',
    roleLabel: 'Role',
    remove: 'Remove',
    you: 'you',
    twoFactorOn: '2FA on',
    twoFactorOff: '2FA off',
    signedInAs: 'Signed in as {email}',
    role: {
      owner: 'Owner',
      admin: 'Admin',
      operator: 'Operator',
      user: 'Editor',
      staff: 'Platform staff',
      viewer: 'Viewer',
    },
    planLabel: 'Plan',
    plan: {
      air: 'Air',
      signal: 'Signal',
      network: 'Network',
    },
  },

  security: {
    passwordTitle: 'Password',
    passwordDetail: 'Choose your own password. Changing it signs out every other session.',
    passwordChange: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password (at least 12 characters)',
    newPasswordRepeat: 'New password again',
    passwordMismatch: 'The two new passwords are not the same.',
    passwordConfirm: 'Change password',
    passwordChanged: 'Password changed. {count} other session(s) signed out.',
    title: 'Account and security',
    intro:
      'Two-factor authentication for your own account. After your password, the panel asks for a code from your authenticator app.',
    account: 'Signed in as',
    stateOn: 'Two-factor on',
    stateOff: 'Two-factor off',
    since: 'Turned on',
    codesLeft: 'Recovery codes left',
    codesLowTitle: 'Almost out of recovery codes',
    codesLowDetail:
      'Issue a fresh set while you can still sign in. With no codes and no phone, you need an administrator.',
    pending: 'An unconfirmed enrolment is waiting. Start again to get a new QR code.',
    enableTitle: 'Turn on two-factor authentication',
    enableDetail:
      'You need an authenticator app: 1Password, Bitwarden, Google Authenticator or the Passwords app on your iPhone all work.',
    enable: 'Turn on',
    confirmPasswordTitle: 'Confirm your password',
    confirmPasswordDetail:
      'An open session is not enough to move your second factor to a different phone.',
    continue: 'Continue',
    scanTitle: 'Scan the code',
    scanDetail:
      'Scan this QR code with your authenticator app, then enter the six-digit code it shows.',
    manualTitle: 'Enter it by hand',
    manualDetail: 'If scanning will not work, type this key into your app instead.',
    activate: 'Confirm and turn on',
    codesTitle: 'Your recovery codes',
    codesWarningTitle: 'Save these codes now',
    codesWarningDetail:
      'This is the only time you will see them. Without these codes and without your phone, only an administrator can let you back in. There is no mail server to send them.',
    codesCopy: 'Copy',
    codesStored: 'I have saved them',
    toOrganisation: 'Your organisation and plan',
    manageTitle: 'Manage',
    reissue: 'New recovery codes',
    reissueDetail: 'A new set makes the old one useless, so throw away any sheet you printed.',
    reissueConfirm: 'Issue a new set',
    disable: 'Turn off',
    disableDetail:
      'Your password and a valid code, both. After this, only your password protects the account.',
    disableConfirm: 'Turn two-factor off',
    errors: {
      codeInvalid: 'That verification code is not right.',
      codeReplayed: 'That code has just been used. Wait for your app to show the next one.',
      passwordWrong: 'That password is not right.',
    },
  },

  users: {
    title: 'Users & permissions',
    intro:
      'Panel accounts. A new or reset password appears exactly once; it is stored only as a hash and can never be looked up again.',
    columnName: 'Name',
    columnRole: 'Role',
    columnTotp: 'Two-step verification',
    columnLastLogin: 'Last signed in',
    columnState: 'State',
    caption: 'Accounts',
    emptyTitle: 'No accounts',
    emptyDetail: 'Create an account to give somebody access to the panel.',
    create: 'New account',
    createConfirm: 'Create account',
    fieldEmail: 'Email address',
    fieldName: 'Name',
    fieldRole: 'Role',
    role: {
      viewer: 'Viewer',
      user: 'Editor',
      operator: 'Operator',
      admin: 'Administrator',
      superadmin: 'Superadmin',
    },
    totpOn: 'On',
    totpReset: 'Clear two-factor',
    totpResetConfirm: 'Clear it and sign the account out',
    totpOff: 'Off',
    never: 'Never',
    inactive: 'Inactive',
    deactivate: 'Deactivate',
    deactivateConfirm: 'Deactivate account',
    reactivate: 'Reactivate',
    resetPassword: 'Reset password',
    resetConfirm: 'Issue a new password',
    changeRole: 'Change role to {role}',
    changeRoleConfirm: 'Change role',
    reasonLabel: 'Reason (recorded in the audit log)',
    passwordIssued: 'Password for {email}',
    passwordOnce: 'Shown once and never retrievable afterwards. Hand it over securely.',
    passwordDismiss: 'I have noted it',
  },

  streamers: {
    title: 'DJs / streamers',
    intro:
      'Source credentials for the live ingest. A presenter’s encoder signs in with these — not the panel. A lost password is reset, never looked up.',
    columnName: 'Name',
    columnUsername: 'Username',
    columnShow: 'Show',
    columnRecord: 'Recording',
    columnState: 'State',
    caption: 'Streamer accounts',
    emptyTitle: 'No DJ accounts yet',
    emptyDetail: 'Create an account so a presenter can go live.',
    create: 'New DJ',
    createConfirm: 'Create DJ',
    fieldName: 'Name',
    fieldUsername: 'Source username',
    fieldShow: 'Bound show',
    fieldRecord: 'Record sessions',
    anyShow: 'Any show',
    showsHint:
      'A presenter with shows can only go live while one of them is on the schedule, from a quarter of an hour before its slot until ten minutes after; the station takes them off the air when it ends. A presenter without shows may connect whenever their windows allow.',
    editShows: 'Shows…',
    recordOn: 'Recording on',
    recordOff: 'Recording off',
    resetCredential: 'Reset password',
    disable: 'Block',
    disableConfirm: 'Block account',
    enable: 'Unblock',
    enableConfirm: 'Unblock account',
    credentialIssued: 'Source credential for {username}',
    credentialOnce: 'Shown once. The DJ enters this as the password in the encoder.',
  },

  requests: {
    title: 'Requests',
    intro:
      'What listeners have asked for. The engine has already applied the separation rules, the eligibility windows and any bans, so a request waiting here is one that passed those and needs a person.',
    columnWhen: 'Asked',
    columnTrack: 'Track',
    columnRequester: 'Requester',
    columnStatus: 'Status',
    columnAction: 'Action',
    caption: 'Song requests',
    emptyTitle: 'Nothing waiting',
    emptyDetail: 'Requests from the station page appear here as listeners make them.',
    filterAll: 'All',
    approve: 'Approve',
    reject: 'Reject',
    status: {
      PENDING: 'Waiting',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      PLAYED: 'Played',
      EXPIRED: 'Expired',
    },
  },
  recordings: {
    title: 'Recordings',
    intro:
      'Airchecks of live sessions. A completed recording is an ordinary media asset: it goes through the same analysis and the same storage as everything else.',
    columnWhen: 'Recorded',
    columnStreamer: 'DJ',
    columnFile: 'File',
    columnLength: 'Length',
    columnSize: 'Size',
    columnStatus: 'Analysis',
    caption: 'Recordings',
    emptyTitle: 'No recordings yet',
    emptyDetail: 'Once a DJ goes live with recording on, the aircheck appears here.',
  },

  liveListeners: {
    title: 'Listeners now',
    intro:
      'Straight from the stream monitor’s samples. The chart puts “now” in context: forty listeners means something different at 03:00 than at drive time.',
    now: 'Listening now',
    peak3h: 'Peak (3 hours)',
    perMount: 'Per stream',
    noMounts: 'No samples yet.',
    lastThreeHours: 'Last three hours',
    noHistory: 'No history in this window yet.',
    chartLabel: 'Listeners over the last three hours, peak {peak}',
    peakShort: 'peak',
    stale: 'The latest sample is from {time}, more than two minutes ago; “now” is unknown.',
  },

  jobs: {
    title: 'Jobs',
    intro:
      '“Analysis is slow” and “analysis is dead” are two different evenings. This page shows what the background workers are doing; intervention happens at the worker, not with a button here.',
    columnQueue: 'Queue',
    columnActive: 'Active',
    columnWaiting: 'Waiting',
    columnDelayed: 'Delayed',
    columnCompleted: 'Completed',
    columnFailed: 'Failed',
    caption: 'Background queues',
    emptyTitle: 'No queues visible',
    emptyDetail: 'The queue information could not be read.',
    failuresIn: 'Recent failures in {queue}',
    attempts: '{count} attempts',
  },

  streamHealth: {
    title: 'Stream health',
    intro:
      'The audio path end to end. The runtime status comes from Liquidsoap; the alerts come from the monitor that listens like a listener. When they disagree, believe the monitor.',
    runtimeUnreachable: 'The playout runtime is unreachable. The stream itself may still be fine.',
    fallbackActive: 'The fallback is carrying the broadcast. Scheduled audio should be playing.',
    emergencyMode: 'The emergency source is active.',
    mode: 'Mode',
    uptime: 'Runtime up {uptime}',
    live: 'Live source',
    liveConnected: 'Connected',
    liveIdle: 'None',
    fallback: 'Fallback',
    fallbackOn: 'Active',
    fallbackOff: 'Standing by',
    outputs: 'Outputs',
    connected: 'Connected',
    disconnected: 'Disconnected',
    noOutputs: 'No outputs reported.',
    queues: 'Runtime queues',
    queueItems: '{count} items',
    alerts: 'Open stream alerts',
    noAlerts: 'No stream alert is open. This is how it should be.',
  },

  podcastFeeds: {
    title: 'Podcasts',
    intro:
      'A feed belongs to a show and publishes its aired episodes. Publishing is a switch, not a side effect: fill the feed first, then open it up.',
    columnTitle: 'Feed',
    columnShow: 'Show',
    columnCategory: 'Category',
    columnTrigger: 'Publication',
    columnState: 'State',
    caption: 'Podcast feeds',
    emptyTitle: 'No feeds yet',
    emptyDetail: 'Create a feed to offer episodes as a podcast.',
    create: 'New feed',
    createConfirm: 'Create feed',
    fieldShow: 'Show',
    chooseShow: 'Choose a show',
    fieldTitle: 'Title',
    fieldSlug: 'Slug',
    fieldDescription: 'Description',
    fieldAuthor: 'Author',
    fieldOwnerName: 'Owner',
    fieldOwnerEmail: 'Owner email address',
    fieldCategory: 'Category',
    published: 'Published',
    unpublished: 'Not published',
    publish: 'Publish',
    unpublish: 'Unpublish',
  },

  distribution: {
    title: 'Distribution',
    intro:
      'Directories subscribe to the feed URL and to nothing else. This page exists to hand that URL over correctly to Apple, Spotify and every app that follows them.',
    publishedFeeds: 'Published feeds',
    nonePublished: 'No published feed yet. Publish one on the podcasts page.',
    unpublishedFeeds: 'Not yet published',
    publishFirst: 'Publish the feed before submitting it to a directory.',
    copy: 'Copy URL',
    copied: 'Copied',
    webhooks: 'Webhooks',
    webhooksIntro: 'External systems notified on events such as a new publication.',
    noWebhooks: 'No webhooks configured.',
  },

  podcastAnalytics: {
    title: 'Podcast analytics',
    intro:
      'Downloads per episode, with IP addresses reduced to pseudonyms before anything is stored. “Partial” is ranged requests that never fetched the bulk of the file — apps preloading, not listeners.',
    feed: 'Feed',
    total: '{count} downloads in this window',
    columnEpisode: 'Episode',
    columnDownloads: 'Downloads',
    columnPartial: 'Partial',
    caption: 'Downloads per episode',
    emptyTitle: 'No downloads yet',
    emptyDetail: 'Once an episode is fetched, the numbers appear here.',
  },

  audienceAnalytics: {
    title: 'Audience analytics',
    intro:
      'Derived from anonymised listener sessions: IP addresses become pseudonyms before anything is stored. The content table uses what actually aired — a show that started late reached its audience late.',
    uniqueListeners: 'Unique listeners',
    peakConcurrent: 'Peak concurrent',
    averageSession: 'Avg. session',
    listeningHours: 'Listening hours',
    overTime: 'Over time',
    chartLabel: 'Average listeners over the chosen window',
    byPlayer: 'By player',
    byCountry: 'By country',
    byMount: 'By stream',
    byContent: 'By broadcast',
    columnContent: 'Broadcast',
    columnAired: 'Aired',
    columnAverage: 'Average',
    columnPeak: 'Peak',
    caption: 'Listeners per aired item',
    emptyTitle: 'No data yet',
    emptyDetail: 'Nothing aired or was measured in this window.',
  },

  archive: {
    title: 'Broadcast archive',
    intro:
      'What actually went out, day by day, from the as-run log. What was planned but never aired is exactly what does not belong here.',
    day: 'Day',
    total: '{count} items · {duration}',
    columnTime: 'Time',
    columnWhat: 'Broadcast',
    columnType: 'Kind',
    columnLength: 'Length',
    caption: 'As-run log',
    emptyTitle: 'Nothing in the archive for this day',
    emptyDetail: 'There is no as-run record for this day.',
    airplayTitle: 'Airplay report (ISRC)',
    airplayIntro:
      'Every song on air in a period, with time, duration, artist, title and ISRC, as CSV for Buma/Stemra and Sena. A song without a code is listed on artist and title.',
    airplayFrom: 'From',
    airplayTo: 'To (inclusive)',
    airplayDownload: 'Download CSV',
  },

  uploads: {
    title: 'Uploads',
    intro:
      'Drop audio here. Every file is analysed after arrival; only when analysis passes does it become schedulable, and what does not decode is quarantined instead of aired. A ShioCaster export (mp3 + .metadata.json with the same name) is recognised as one pack: its title track then drives the stream title during the broadcast. A plain mp3 works as it always did.',
    dropLabel: 'Upload audio files',
    dropHere: 'Drop audio here or click to choose',
    dropDetail:
      'Multiple files at once is fine. A .metadata.json next to the mp3 is paired automatically. Duplicates are recognised and not stored twice.',
    inProgress: 'In progress',
    state: {
      uploading: 'Uploading',
      processing: 'Processing',
      done: 'Done',
      duplicate: 'Already existed',
      failed: 'Failed',
    },
    recent: 'Recent uploads',
    columnFile: 'File',
    columnWhen: 'Uploaded',
    columnAnalysis: 'Analysis',
    caption: 'Recent uploads',
    emptyTitle: 'No uploads yet',
    emptyDetail: 'Upload audio to fill the library.',
    columnPack: 'Episode pack',
    packNone: 'None (classic)',
    packMismatch: 'Duration mismatch',
    packTitles: '{count} title changes',
    packUnknownSongs: '{count} song(s) without a name',
    packNoAudio:
      'No uploaded audio named “{stem}” was found. Upload the mp3 first or together with this file.',
  },

  fallback: {
    title: 'Fallback & emergency',
    intro:
      'The chain is: scheduled → rotation → emergency source → Icecast’s own fallback mount. This page shows which link is carrying the broadcast now, and holds the emergency switches.',
    emergencyActive: 'The emergency source is active and overrides every schedule.',
    fallbackCarrying: 'The fallback is carrying the broadcast.',
    mode: 'Mode',
    chain: 'What is carrying now',
    chainScheduled: 'Scheduled audio',
    chainUnknown: 'The runtime could not be asked',
    chainLive: 'Live source',
    chainFallback: 'Fallback',
    chainEmergency: 'Emergency source',
    emergencyControls: 'Emergency source',
    emergencyDetail:
      'Activating interrupts the programming and puts the emergency set on air. Both directions require a reason and are recorded.',
    standDown: 'Back to automation',
    settings: 'Settings',
    settingEnabled: 'Emergency source available',
    settingSilence: 'Silence alarm after',
    settingTimeout: 'Source timeout',
    settingFreeze: 'Freeze window before air',
    settingsNote:
      'The thresholds themselves are managed on the settings page; do not change them during an incident.',
    toggle: 'Toggle',
  },

  settings: {
    title: 'Settings',
    intro:
      'Per section, per key. What the platform derives from its own behaviour, the API refuses to change — that is policy, not a missing input. Every change lands in the audit log.',
    section: 'Section',
    sections: {
      broadcast: 'Broadcast',
      streams: 'Streams',
      playout: 'Playout',
      live_studio: 'Live studio',
      storage: 'Storage',
      audio_processing: 'Audio processing',
      reliability: 'Reliability',
      integrations: 'Integrations',
    },
    emptySection: 'This section has no settings yet.',
    saved: 'Saved',
  },

  clocks: {
    title: 'Clocks',
    intro:
      'The shape of an hour. The dial is how you think about an hour; the table is how you make it precise. An exact-time item starts on its minute whatever the rest of the hour did.',
    which: 'Clocks',
    off: 'Off',
    create: 'New clock',
    createConfirm: 'Create clock',
    fieldName: 'Name',
    fieldSlug: 'Slug',
    empty: 'No clocks yet. Create one to give an hour its shape.',
    minutesUsed: '{used} of 60 minutes filled',
    overAnHour: 'More than an hour',
    enable: 'Enable',
    disable: 'Disable',
    assignments: 'Assignments',
    noAssignments: 'Not assigned to any hours yet.',
    everyDay: 'Every day',
    day: { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' },
    elements: 'Elements',
    columnStart: 'Start (min)',
    columnDuration: 'Length (min)',
    columnLabel: 'Label',
    columnType: 'Kind',
    columnExact: 'Exact',
    columnFlexible: 'Flexible',
    columnActions: 'Actions',
    newElement: 'New element',
    addElement: 'Add element',
    remove: 'Remove',
    exactNote:
      'Exact = starts on the minute, whatever overran. Flexible = may be shortened to absorb overrun.',
    dialLabel: 'Radio clock: the shape of one broadcast hour',
    hub: 'HOUR',
  },

  liveStudio: {
    title: 'Live studio',
    intro:
      'Who is connected now, how to connect, and the one destructive control: disconnecting a live source, which hands the station back to automation.',
    now: 'Connected now',
    nobody: 'No live source is connected. Automation is carrying the broadcast.',
    since: 'Connected since',
    software: 'Software',
    format: 'Format',
    disconnect: 'Disconnect',
    disconnectConfirm: 'Disconnect source',
    howToConnect: 'How to connect',
    connectIntro:
      'Any encoder speaking the Icecast source protocol works: BUTT, Mixxx, OBS with an Icecast output.',
    server: 'Server',
    port: 'Port',
    mount: 'Mount',
    credentials: 'Credentials',
    credentialsDetail: 'The DJ’s own username and the once-shown password.',
    takeoverNote:
      'An authorised source takes over immediately; when it drops, automation returns by itself. Silence is never the outcome.',
    studioTitle: 'ShioCaster and AzuraCast-compatible software',
    studioIntro:
      'The panel answers the AzuraCast API that ShioCaster and similar software speak. Paste the stream URL below into the studio; it derives the station and the API from it.',
    studioStreamUrl: 'Stream URL for the studio',
    studioLive: 'Live input (host:port · mount)',
    studioNowPlaying: 'Now playing',
    studioTitlePush: 'Title push (Broadcast OS mode)',
    studioKeys: 'API keys',
    studioKeysDetail:
      'The skip button needs a key with the playout:control scope; the title push needs live:broadcast. Both go in the X-API-Key header.',
    studioKeysLink: 'Issue a key',
    studioNote:
      'While a presenter is live, the studio sends every title change to the live input with the DJ credentials; the listener sees it at once. The ISRC in a title push is recorded for the airplay report and never reaches the stream.',
    history: 'Sessions',
    columnConnected: 'When',
    columnWho: 'DJ',
    columnStatus: 'Status',
    columnFormat: 'Format',
    columnRecording: 'Recording',
    recorded: 'Recorded',
    caption: 'Live sessions',
    emptyTitle: 'No live sessions yet',
    emptyDetail: 'Once a DJ connects, the session appears here.',
    status: {
      CONNECTED: 'Connected',
      DISCONNECTED: 'Disconnected',
    },
  },

  notifications: {
    title: 'SMS & telephone alerting',
    intro:
      'Who gets called when something goes wrong. An alert is texted to everyone on the list; a critical alert nobody acknowledges becomes a phone call. Reply OK to the text or press 1 during the call to acknowledge. The provider credentials live on the server, not here.',
    providerTitle: 'Provider',
    providerState: 'State',
    providerConfigured: 'Twilio connected',
    providerNotConfigured: 'Not configured',
    providerSmsFrom: 'SMS sender',
    providerVoiceFrom: 'Calling number',
    providerWebhooks: 'Webhook for inbound SMS',
    providerWebhooksHint:
      'Set this address as “A message comes in” on the Twilio number. Delivery status and keypresses arrive on /status and /voice/ack under the same base.',
    providerMissingTitle: 'Nothing is being sent: Twilio is not configured',
    providerMissingDetail:
      'Set TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN and TWILIO_SMS_FROM in the .env file on the server and restart the API and the worker. Until then notifications are recorded but not delivered. Broadcast never depends on this.',
    policyTitle: 'Policy',
    policySms: 'Text when an alert is raised',
    policyVoice: 'Call when a critical alert stays unacknowledged',
    policyVoiceAfter: 'Call after (minutes)',
    policyAllClear: 'Text when the alert is resolved',
    policyLanguage: 'Language of messages and calls',
    policySaved: 'Policy saved',
    create: 'Add contact',
    createConfirm: 'Add',
    fieldName: 'Name',
    fieldPhone: 'Telephone number',
    fieldThreshold: 'From severity',
    fieldSms: 'SMS',
    fieldVoice: 'Call',
    thresholdCritical: 'Critical',
    thresholdWarning: 'Warning and above',
    columnName: 'Name',
    columnPhone: 'Number',
    columnThreshold: 'From',
    columnSms: 'SMS',
    columnVoice: 'Call',
    columnState: 'State',
    contactsCaption: 'Contacts',
    emptyTitle: 'No contacts yet',
    emptyDetail: 'Without contacts nobody is called. Add at least one number.',
    on: 'On',
    off: 'Off',
    testSms: 'Test SMS',
    testCall: 'Test call',
    testSmsSent: 'Test SMS sent to {name}',
    testCallPlaced: 'Test call placed to {name}',
    testFailed: 'The test was not sent.',
    enable: 'Enable',
    disable: 'Disable',
    remove: 'Remove',
    removeConfirm: 'Remove contact',
    deliveriesTitle: 'Sent',
    deliveriesCaption: 'Recent messages and calls',
    deliveriesEmptyTitle: 'Nothing sent yet',
    deliveriesEmptyDetail:
      'Every message and call appears here, including the ones that failed to deliver.',
    columnWhen: 'When',
    columnChannel: 'Channel',
    columnKind: 'Kind',
    columnAlert: 'Alert',
    columnStatus: 'Status',
    channelSms: 'SMS',
    channelVoice: 'Call',
    kinds: {
      raised: 'Raised',
      escalated: 'Escalation',
      cleared: 'Resolved',
      test: 'Test',
    },
    statuses: {
      pending: 'Queued',
      retry: 'Retrying',
      sent: 'Sent',
      failed: 'Failed',
      abandoned: 'Dropped',
    },
  },

  apiKeys: {
    title: 'API keys',
    intro:
      'A key is long-lived access for an integration: the Prometheus scrape, a script, a device that polls the API. It acts as you with only the rights you choose, and stops working the moment you revoke it or your account is deactivated. The key itself is shown once.',
    create: 'New key',
    createConfirm: 'Create key',
    fieldName: 'Name',
    namePlaceholder: 'e.g. prometheus',
    fieldExpiry: 'Expires after (days, empty = never)',
    fieldScopes: 'Rights',
    scopesHint: 'Only rights you hold yourself. Choose as few as possible.',
    columnAccess: 'Access',
    fieldAccess: 'Limit to shows and presenters',
    accessHint:
      'Nothing ticked: the key acts as you do. Anything ticked: the key sees and acts on the ticked shows and presenters only, their episodes and sessions, and the air while one of them is on it. This is what a studio or a single presenter’s software should get.',
    accessShows: 'Shows',
    accessStreamers: 'Presenters',
    accessAll: 'Everything you can',
    columnName: 'Name',
    columnPrefix: 'Starts with',
    columnScopes: 'Rights',
    columnLastUsed: 'Last used',
    columnExpires: 'Expires',
    columnState: 'State',
    caption: 'API keys',
    emptyTitle: 'No keys yet',
    emptyDetail: 'Create one for an integration that has to work without a sign-in session.',
    neverUsed: 'Never',
    noExpiry: 'Never',
    revoke: 'Revoke',
    revokeConfirm: 'Revoke key',
    revokedOn: 'Revoked {date}',
    issued: 'Key “{name}” created',
    issuedOnce: 'Shown once. Save it now; only its beginning is visible afterwards.',
  },

  playlistStudio: {
    title: 'Playlist Studio',
    intro:
      'The AI playlist generator, per show and per episode. Pick the show and, if you like, the episode you are preparing; the studio opens with that brief filled in and with the house rules, seed artists and show clock of that same show. What it publishes on Spotify is listed below against the episode.',
    notConfiguredTitle: 'Playlist Studio is not running on this server',
    notConfiguredDetail:
      'Start the overlay compose.playlist-studio.yml with PLAYLIST_STUDIO_TOKEN and PLAYLIST_STUDIO_WEBHOOK_SECRET in .env. Set the Spotify and LLM keys once afterwards in the studio’s own Settings page, here inside the panel.',
    unreachableTitle: 'Playlist Studio is configured but not answering',
    fieldShow: 'Show',
    fieldEpisode: 'Episode',
    noEpisode: 'No episode (the show only)',
    reachable: 'Connected ({version})',
    unreachable: 'Unreachable',
    spotifyConnected: 'Spotify connected',
    spotifyNotConnected: 'Spotify not connected yet',
    llmConfigured: 'AI configured',
    llmNotConfigured: 'AI not configured yet',
    open: 'Open the studio',
    briefShow: 'The studio opens with the profile and house rules of {show}.',
    briefEpisode:
      'The studio opens for {show}, episode {episode}: title, air date and target duration are filled in.',
    frameTitle: 'Playlist Studio',
    published: 'Playlist “{name}” published on Spotify',
    playlistsTitle: 'Playlists of this show',
    playlistsCaption: 'Published playlists',
    playlistsEmptyTitle: 'No playlists yet',
    playlistsEmptyDetail:
      'Generate and publish one in the studio; it appears here against the episode.',
    columnWhen: 'When',
    columnName: 'Name',
    columnEpisode: 'Episode',
    columnTracks: 'Tracks',
    columnLink: 'Spotify',
    openSpotify: 'Open',
    replaced: 'Updated',
    minutes: '{count} min',
  },

  notFound: {
    nothingHere: 'Nothing here',
    notMatched: 'That address does not match a page in the panel.',
    title: 'Page not found',
    body: 'This page does not exist.',
    back: 'Back to on air',
  },
} as const;
